/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

/*
Data selection block for fires
*/
import * as Blockly from 'blockly/core';
import 'blockly/python';
import { AreaService } from '@/services/area.service';
import { getAllowedStatements } from '../block_compatibility_checks';
import { dataset } from '../dataset';
import { CustomFieldDate } from '../../blocklyFieldDate';
import { areaChange } from '../helper_functions';

const landcoverOptions = [
    ["None", "none"],
    ["Copernicus Land Cover", "copernicus_landcover"]
];

Blockly.Blocks['input_fire_data'] = {
    init: function () {
        this.prev_date = '';
        var area_dropdown = AreaService.getAreas();
        this.appendDummyInput()
            .appendField('Fires: ')
            .appendField(new Blockly.FieldDropdown([
                ['Annual Burnt Area', 'annual_burned_area'],
                ['Fires Temperature [Temperature in K]', 'firms'],
                ['Fires Confidence [Confidence]', 'terra_fires'],
            ], this.validateFireData), "dropdown");
        this.appendDummyInput('area_controls')
            .appendField("Area: ", 'area_text')
            .appendField(new Blockly.FieldDropdown(area_dropdown), "area_dropdown")
        this.appendDummyInput('inp2')
            .appendField("From:")
            .appendField(new CustomFieldDate(dataset.setFrom(this.getFieldValue('dropdown')),
                function (newValue) { return newValue }, 'true'), 'fd')
            .appendField('To:')
            .appendField(new CustomFieldDate(dataset.getTo(this.getFieldValue('dropdown')),
                function (newValue) { return newValue }, 'true'), 'td');
        this.appendStatementInput("do")
            .setCheck(getAllowedStatements('fires_droughts'))

        this.setInputsInline(false);
        this.setStyle('fire');
        this.setTooltip(Blockly.Msg.FIRES_BLOCK_TOOLTIP);
        this.setHelpUrl("");
        this.previous_dataset = 'firms';
        this.previous_area = 'polygon';
        this.setOnChange(() => {
            if (this.getFieldValue('dropdown') !== this.previous_dataset) {
                this.updateShape_();
                this.previous_dataset = this.getFieldValue('dropdown');
            }
        });

        this.areaSubscription = AreaService.areaChange$.subscribe(() => {
            let result = areaChange(this, "area_controls", "area_dropdown");
            if(!result) {
                this.areaSubscription.unsubscribe();
            }
        });
    },

    addLandCoverOptions: function(block, options) {
        block.appendDummyInput("second_data_input")
            .appendField(new Blockly.FieldLabelSerializable("Land Cover Breakdown: "), "secondary_data_text")
            .appendField(new Blockly.FieldDropdown(options), "secondary_data")

        block.moveInputBefore("second_data_input", "do")
    },

    removeLandCoverOptions: function(block) {
        block.removeInput("second_data_input")
    },

    validateFireData: function(newValue){
        let thisBlock = this.getSourceBlock();
        let landcoverInput = thisBlock.getInput('second_data_input');
        
        thisBlock.showTimeseriesCheckbox = newValue === 'annual_burned_area';
        if (newValue === 'annual_burned_area') {
            if (!landcoverInput) {
                thisBlock.addLandCoverOptions(thisBlock, landcoverOptions)
            }
        } else if (landcoverInput) {
            thisBlock.removeLandCoverOptions(thisBlock);
        }

        thisBlock.updateTimeseriesCheckbox();
        return newValue;
    },

    updateShape_: function () {
        var from = this.getField('fd');
        let sat = this.getFieldValue('dropdown');
        if (sat === 'annual_burned_area') {
            from.setValue(dataset[sat]['from']);
        } else {
            from.setValue(dataset.setFrom(this.getFieldValue('dropdown')));
        }
        var to = this.getField('td');
        to.setValue(dataset.getTo(this.getFieldValue('dropdown')));
    },

    /**
     * called whenever the user selects a dropdown option, checks boolean showTimeseriesCheckbox to see if the
     * timeseriesControls fields need to be added/removed from the block
     */
    updateTimeseriesCheckbox: function() {
        if(this.showTimeseriesCheckbox && !this.getInput("timeseriesControls")) {
            this.appendDummyInput("timeseriesControls")
                .setAlign(Blockly.ALIGN_LEFT);

            // move new dummy input to before the C block section
            this.moveInputBefore("timeseriesControls", "do");

            let timeseriesControls = this.getInput("timeseriesControls");
            timeseriesControls
                .appendField("Show me a timeseries animation")
                .appendField(new Blockly.FieldCheckbox(false), 'isTimeseries')
        } else if (!this.showTimeseriesCheckbox && this.getInput("timeseriesControls")) {
            // remove input
            this.removeInput("timeseriesControls")
        }
    }
};
